import React, { useState } from 'react';
import './SpyDataPreview.css';

/**
 * Render an email value with a Blacklist marker if it matched the user's
 * blacklist. Centralized so all email-rendering spots in this preview
 * (search results, AI summary, scraped data, etc.) get the same treatment.
 *
 * The check is case-insensitive: blacklist entries are stored lowercase.
 */
const EmailValue = ({ email, blockedSet, className = '' }) => {
  if (!email) return null;
  const isBlocked = blockedSet && typeof email === 'string' && blockedSet.has(email.trim().toLowerCase());
  if (!isBlocked) return <span className={className}>{email}</span>;
  return (
    <span className={`${className} sdp-email-blacklisted`} title="On your blacklist — will be skipped on Apply">
      <span className="sdp-email-blacklisted-text">{email}</span>
      <span className="sdp-email-blacklisted-badge"><i className="fa fa-ban" /> Blacklist</span>
    </span>
  );
};

const SpyDataPreview = ({ 
  isOpen, 
  onClose, 
  onApply, 
  intelligenceData, 
  contactData,
  onSelectiveApply,
  blacklistedEmails,
}) => {
  if (!isOpen || !intelligenceData) return null;

  // Normalize once for the whole render. Empty Set when nothing is blocked
  // — EmailValue then becomes a no-op, no styling overhead.
  const blockedSet = blacklistedEmails instanceof Set
    ? blacklistedEmails
    : new Set(Array.isArray(blacklistedEmails) ? blacklistedEmails.map(e => String(e).trim().toLowerCase()) : []);
  const blockedCount = blockedSet.size;

  const { 
    searchResults = {},
    rawSearchResults = {},
    aiAnalysis = {},
    scrapingResults = null,
    searchTerm = '',
    sources = []
  } = intelligenceData;

  // Use rawSearchResults if available, otherwise fallback to searchResults
  const results = rawSearchResults.organic || searchResults.organic || [];
  const knowledgeGraph = rawSearchResults.knowledgeGraph || searchResults.knowledgeGraph || null;
  
  // AI Analysis data
  const { 
    socialMediaLinks = {}, 
    prioritizedWebsites = [], 
    businessSummary = '' 
  } = aiAnalysis;
  
  // Scraping data
  const scrapedData = scrapingResults?.data || null;
  
  return (
    <div className="spy-data-preview-overlay">
      <div className="spy-data-preview-modal">
        <div className="spy-preview-header">
          <div className="spy-preview-title">
            <h2>🔍 Google Search Results</h2>
            <div className="spy-preview-subtitle">
              Search term: <strong>"{searchTerm}"</strong> for {contactData?.business_name || 'Unknown Business'}
            </div>
          </div>
          <button className="spy-preview-close" onClick={onClose}>×</button>
        </div>

        <div className="spy-preview-content">
          {blockedCount > 0 && (
            <div className="sdp-blacklist-banner">
              <i className="fa fa-ban" />
              <div className="sdp-blacklist-banner-text">
                <strong>{blockedCount} email{blockedCount === 1 ? '' : 's'} on your blacklist</strong>
                <span> — marked below. {blockedCount === 1 ? 'It' : 'They'} will be skipped on Apply.</span>
              </div>
            </div>
          )}
          {/* Knowledge Graph */}
          {knowledgeGraph && (
            <div className="spy-data-section knowledge-graph">
              <div className="section-header">
                <h3>📊 Knowledge Graph</h3>
              </div>
              <div className="knowledge-graph-content">
                <div className="kg-title">{knowledgeGraph.title}</div>
                {knowledgeGraph.type && <div className="kg-type">{knowledgeGraph.type}</div>}
                {knowledgeGraph.description && <div className="kg-description">{knowledgeGraph.description}</div>}
                {knowledgeGraph.website && (
                  <div className="kg-website">
                    <strong>Website:</strong> 
                    <a href={knowledgeGraph.website} target="_blank" rel="noopener noreferrer">
                      {knowledgeGraph.website}
                    </a>
                  </div>
                )}
                {knowledgeGraph.attributes && Object.keys(knowledgeGraph.attributes).length > 0 && (
                  <div className="kg-attributes">
                    <strong>Attributes:</strong>
                    <ul>
                      {Object.entries(knowledgeGraph.attributes).map(([key, value], index) => (
                        <li key={index}><strong>{key}:</strong> {value}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
                     )}

          {/* AI Content Analysis - Enhanced Intelligence */}
          {intelligenceData.aiContentAnalysis && (
            <div className="spy-data-section ai-content-analysis">
              <div className="section-header">
                <h3>🧠 AI Intelligence Analysis</h3>
                <div className="confidence-score">
                  Confidence: {intelligenceData.aiContentAnalysis.confidenceScore || 0}%
                </div>
              </div>
              
              <div className="ai-intelligence-content">
                {/* Detailed Summary */}
                {intelligenceData.aiContentAnalysis.detailedSummary && (
                  <div className="intelligence-summary">
                    <h4>📋 Detailed Intelligence Summary</h4>
                    <div className="summary-text">
                      {intelligenceData.aiContentAnalysis.detailedSummary}
                    </div>
                  </div>
                )}

                <div className="intelligence-grid">
                  {/* Contact Summary */}
                  {intelligenceData.aiContentAnalysis.contactSummary && (
                    <div className="intelligence-card contact-summary">
                      <h4>📞 Contact Information</h4>
                      <div className="contact-info">
                        {intelligenceData.aiContentAnalysis.contactSummary.emails?.length > 0 && (
                          <div className="contact-section">
                            <strong>📧 Emails:</strong>
                            <ul>
                              {intelligenceData.aiContentAnalysis.contactSummary.emails.map((email, index) => {
                                const e = typeof email === 'string' ? email : email?.email;
                                return <li key={index}><EmailValue email={e} blockedSet={blockedSet} /></li>;
                              })}
                            </ul>
                          </div>
                        )}
                        {intelligenceData.aiContentAnalysis.contactSummary.phones?.length > 0 && (
                          <div className="contact-section">
                            <strong>📱 Phones:</strong>
                            <ul>
                              {intelligenceData.aiContentAnalysis.contactSummary.phones.map((phone, index) => (
                                <li key={index}>{phone}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {intelligenceData.aiContentAnalysis.contactSummary.addresses?.length > 0 && (
                          <div className="contact-section">
                            <strong>📍 Addresses:</strong>
                            <ul>
                              {intelligenceData.aiContentAnalysis.contactSummary.addresses.map((address, index) => (
                                <li key={index}>{address}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {intelligenceData.aiContentAnalysis.contactSummary.businessHours && (
                          <div className="contact-section">
                            <strong>🕒 Business Hours:</strong>
                            <p>{intelligenceData.aiContentAnalysis.contactSummary.businessHours}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Social Media Profiles */}
                  {intelligenceData.aiContentAnalysis.socialMediaProfiles && 
                   Object.values(intelligenceData.aiContentAnalysis.socialMediaProfiles).some(Boolean) && (
                    <div className="intelligence-card social-summary">
                      <h4>📱 Social Media Profiles</h4>
                      <div className="social-profiles">
                        {Object.entries(intelligenceData.aiContentAnalysis.socialMediaProfiles).map(([platform, url]) => (
                          url && (
                            <div key={platform} className="social-item">
                              <span className="platform-icon">
                                {platform === 'instagram' && '📷'}
                                {platform === 'facebook' && '👥'}
                                {platform === 'linkedin' && '💼'}
                                {platform === 'tiktok' && '🎵'}
                                {platform === 'twitter' && '🐦'}
                                {platform === 'youtube' && '📺'}
                              </span>
                              <div className="platform-info">
                                <span className="platform-name">{platform.charAt(0).toUpperCase() + platform.slice(1)}</span>
                                <a href={url} target="_blank" rel="noopener noreferrer" className="profile-link">
                                  {url.length > 40 ? url.substring(0, 40) + '...' : url}
                                </a>
                              </div>
                            </div>
                          )
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Key Highlights */}
                  {intelligenceData.aiContentAnalysis.keyHighlights?.length > 0 && (
                    <div className="intelligence-card highlights-summary">
                      <h4>✨ Key Highlights</h4>
                      <div className="highlights-list">
                        {intelligenceData.aiContentAnalysis.keyHighlights.map((highlight, index) => (
                          <div key={index} className="highlight-item">
                            <span className="highlight-bullet">•</span>
                            <span className="highlight-text">{highlight}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Focused module cards — Shop Intelligence, Company Profile & SEO Audit.
              Each card renders only when the corresponding module ran AND
              returned non-empty data, so users never see empty placeholders. */}
          {(intelligenceData.shopIntelligence || intelligenceData.companyProfile || intelligenceData.seoAudit) && (
            <div className="sdp-focus-grid">
              {intelligenceData.shopIntelligence && intelligenceData.shopIntelligence.isShop !== false && (
                <div className="sdp-focus-card sdp-focus-card-shop">
                  <div className="sdp-focus-card-head">
                    <span className="sdp-focus-card-icon"><i className="fa fa-shopping-cart" /></span>
                    <span className="sdp-focus-card-title">Shop Intelligence</span>
                  </div>
                  <ul className="sdp-focus-card-list">
                    {intelligenceData.shopIntelligence.platform && (
                      <li><span>Platform</span><strong>{intelligenceData.shopIntelligence.platform}</strong></li>
                    )}
                    {intelligenceData.shopIntelligence.productCountRange && (
                      <li>
                        <span>Products</span>
                        <strong>
                          {intelligenceData.shopIntelligence.productCountRange}
                          {intelligenceData.shopIntelligence.productCountEstimate ? ` · ${intelligenceData.shopIntelligence.productCountEstimate}` : ''}
                        </strong>
                      </li>
                    )}
                    {Array.isArray(intelligenceData.shopIntelligence.mainCategories) && intelligenceData.shopIntelligence.mainCategories.length > 0 && (
                      <li><span>Categories</span><strong>{intelligenceData.shopIntelligence.mainCategories.slice(0, 4).join(', ')}</strong></li>
                    )}
                    {Array.isArray(intelligenceData.shopIntelligence.notableBrands) && intelligenceData.shopIntelligence.notableBrands.length > 0 && (
                      <li><span>Brands</span><strong>{intelligenceData.shopIntelligence.notableBrands.slice(0, 4).join(', ')}</strong></li>
                    )}
                    {(intelligenceData.shopIntelligence.foundedYear || intelligenceData.shopIntelligence.shopAgeYears) && (
                      <li>
                        <span>Age</span>
                        <strong>
                          {intelligenceData.shopIntelligence.foundedYear ? `since ${intelligenceData.shopIntelligence.foundedYear}` : ''}
                          {intelligenceData.shopIntelligence.foundedYear && intelligenceData.shopIntelligence.shopAgeYears ? ' · ' : ''}
                          {intelligenceData.shopIntelligence.shopAgeYears ? `${intelligenceData.shopIntelligence.shopAgeYears} y` : ''}
                        </strong>
                      </li>
                    )}
                    {intelligenceData.shopIntelligence.trustScore?.score && (
                      <li>
                        <span>Rating</span>
                        <strong>
                          {intelligenceData.shopIntelligence.trustScore.score}
                          {intelligenceData.shopIntelligence.trustScore.source ? ` · ${intelligenceData.shopIntelligence.trustScore.source}` : ''}
                        </strong>
                      </li>
                    )}
                  </ul>
                  {intelligenceData.shopIntelligence.summary && (
                    <p className="sdp-focus-card-summary">{intelligenceData.shopIntelligence.summary}</p>
                  )}
                </div>
              )}

              {intelligenceData.companyProfile && (
                <div className="sdp-focus-card sdp-focus-card-company">
                  <div className="sdp-focus-card-head">
                    <span className="sdp-focus-card-icon"><i className="fa fa-building" /></span>
                    <span className="sdp-focus-card-title">Company Profile</span>
                  </div>
                  <ul className="sdp-focus-card-list">
                    {(intelligenceData.companyProfile.employeeRange || intelligenceData.companyProfile.employeeExact) && (
                      <li>
                        <span>Size</span>
                        <strong>
                          {intelligenceData.companyProfile.employeeRange || ''}
                          {intelligenceData.companyProfile.employeeRange && intelligenceData.companyProfile.employeeExact ? ' · ' : ''}
                          {intelligenceData.companyProfile.employeeExact ? `~${intelligenceData.companyProfile.employeeExact}` : ''}
                        </strong>
                      </li>
                    )}
                    {intelligenceData.companyProfile.revenueRange && (
                      <li>
                        <span>Revenue</span>
                        <strong>
                          {intelligenceData.companyProfile.revenueRange}
                          {intelligenceData.companyProfile.revenueSource ? ` · ${intelligenceData.companyProfile.revenueSource}` : ''}
                        </strong>
                      </li>
                    )}
                    {intelligenceData.companyProfile.foundedYear && (
                      <li><span>Founded</span><strong>{intelligenceData.companyProfile.foundedYear}</strong></li>
                    )}
                    {intelligenceData.companyProfile.headquarters && (
                      <li>
                        <span>HQ</span>
                        <strong>
                          {intelligenceData.companyProfile.headquarters}
                          {intelligenceData.companyProfile.locationCount ? ` · ${intelligenceData.companyProfile.locationCount} locations` : ''}
                        </strong>
                      </li>
                    )}
                    {intelligenceData.companyProfile.industry && (
                      <li><span>Industry</span><strong>{intelligenceData.companyProfile.industry}</strong></li>
                    )}
                    {typeof intelligenceData.companyProfile.publicCompany === 'boolean' && (
                      <li><span>Type</span><strong>{intelligenceData.companyProfile.publicCompany ? 'Publicly traded' : 'Privately held'}</strong></li>
                    )}
                  </ul>
                  {Array.isArray(intelligenceData.companyProfile.growthSignals) && intelligenceData.companyProfile.growthSignals.length > 0 && (
                    <div className="sdp-focus-card-signals">
                      <span className="sdp-focus-card-signals-label">Growth signals</span>
                      <ul>
                        {intelligenceData.companyProfile.growthSignals.slice(0, 4).map((sig, i) => (
                          <li key={i}>{sig}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {intelligenceData.companyProfile.summary && (
                    <p className="sdp-focus-card-summary">{intelligenceData.companyProfile.summary}</p>
                  )}
                </div>
              )}

              {intelligenceData.seoAudit && intelligenceData.seoAudit.hasWebsite !== false && (
                <div className="sdp-focus-card sdp-focus-card-seo">
                  <div className="sdp-focus-card-head">
                    <span className="sdp-focus-card-icon"><i className="fa fa-magnifying-glass-chart" /></span>
                    <span className="sdp-focus-card-title">SEO Audit</span>
                    {intelligenceData.seoAudit.overallScore && (
                      <span className="sdp-focus-card-badge">{intelligenceData.seoAudit.overallScore}</span>
                    )}
                  </div>
                  <ul className="sdp-focus-card-list">
                    {intelligenceData.seoAudit.titleTag?.status && (
                      <li>
                        <span>Title tag</span>
                        <strong>
                          {intelligenceData.seoAudit.titleTag.status}
                          {intelligenceData.seoAudit.titleTag.value ? ` · ${intelligenceData.seoAudit.titleTag.value.substring(0, 50)}${intelligenceData.seoAudit.titleTag.value.length > 50 ? '…' : ''}` : ''}
                        </strong>
                      </li>
                    )}
                    {intelligenceData.seoAudit.metaDescription?.status && (
                      <li><span>Meta description</span><strong>{intelligenceData.seoAudit.metaDescription.status}</strong></li>
                    )}
                    {intelligenceData.seoAudit.h1?.status && (
                      <li>
                        <span>H1</span>
                        <strong>
                          {intelligenceData.seoAudit.h1.status}
                          {intelligenceData.seoAudit.h1.value ? ` · ${intelligenceData.seoAudit.h1.value.substring(0, 50)}${intelligenceData.seoAudit.h1.value.length > 50 ? '…' : ''}` : ''}
                        </strong>
                      </li>
                    )}
                    {typeof intelligenceData.seoAudit.httpsEnabled === 'boolean' && (
                      <li><span>HTTPS</span><strong>{intelligenceData.seoAudit.httpsEnabled ? 'Enabled' : 'Missing'}</strong></li>
                    )}
                    {intelligenceData.seoAudit.mobileFriendly && (
                      <li><span>Mobile</span><strong>{intelligenceData.seoAudit.mobileFriendly}</strong></li>
                    )}
                    {intelligenceData.seoAudit.pageSpeedHint && (
                      <li><span>Speed</span><strong>{intelligenceData.seoAudit.pageSpeedHint}</strong></li>
                    )}
                    {intelligenceData.seoAudit.indexability && (
                      <li>
                        <span>Indexability</span>
                        <strong>
                          {[
                            intelligenceData.seoAudit.indexability.robotsTxt ? `robots: ${intelligenceData.seoAudit.indexability.robotsTxt}` : null,
                            intelligenceData.seoAudit.indexability.sitemap ? `sitemap: ${intelligenceData.seoAudit.indexability.sitemap}` : null,
                          ].filter(Boolean).join(' · ')}
                        </strong>
                      </li>
                    )}
                    {typeof intelligenceData.seoAudit.structuredData === 'boolean' && (
                      <li><span>Structured data</span><strong>{intelligenceData.seoAudit.structuredData ? 'Found' : 'Missing'}</strong></li>
                    )}
                    {intelligenceData.seoAudit.imageAltTags && (
                      <li><span>Image alt tags</span><strong>{intelligenceData.seoAudit.imageAltTags}</strong></li>
                    )}
                  </ul>
                  {Array.isArray(intelligenceData.seoAudit.criticalIssues) && intelligenceData.seoAudit.criticalIssues.length > 0 && (
                    <div className="sdp-focus-card-signals">
                      <span className="sdp-focus-card-signals-label">Critical issues</span>
                      <ul>
                        {intelligenceData.seoAudit.criticalIssues.slice(0, 4).map((issue, i) => (
                          <li key={i}>{issue}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {Array.isArray(intelligenceData.seoAudit.quickWins) && intelligenceData.seoAudit.quickWins.length > 0 && (
                    <div className="sdp-focus-card-signals">
                      <span className="sdp-focus-card-signals-label">Quick wins</span>
                      <ul>
                        {intelligenceData.seoAudit.quickWins.slice(0, 3).map((win, i) => (
                          <li key={i}>{win}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {intelligenceData.seoAudit.summary && (
                    <p className="sdp-focus-card-summary">{intelligenceData.seoAudit.summary}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* AI Analysis Results */}
          {(businessSummary || Object.values(socialMediaLinks).some(Boolean) || prioritizedWebsites.length > 0) && (
            <>
              {/* Business Summary */}
              {businessSummary && (
                <div className="spy-data-section ai-summary">
                  <div className="section-header">
                    <h3>🤖 AI Business Summary</h3>
                  </div>
                  <div className="ai-summary-content">
                    <p>{businessSummary}</p>
                  </div>
                </div>
              )}

              {/* Social Media Links */}
              {Object.values(socialMediaLinks).some(Boolean) && (
                <div className="spy-data-section social-media">
                  <div className="section-header">
                    <h3>📱 Social Media Profiles Found</h3>
                  </div>
                  <div className="social-media-list">
                    {Object.entries(socialMediaLinks).map(([platform, url]) => (
                      url && (
                        <div key={platform} className="social-media-item">
                          <div className="social-platform">
                            <span className="platform-icon">
                              {platform === 'instagram' && '📷'}
                              {platform === 'facebook' && '👥'}
                              {platform === 'linkedin' && '💼'}
                              {platform === 'tiktok' && '🎵'}
                              {platform === 'twitter' && '🐦'}
                              {platform === 'youtube' && '📺'}
                            </span>
                            <span className="platform-name">{platform.charAt(0).toUpperCase() + platform.slice(1)}</span>
                          </div>
                          <a href={url} target="_blank" rel="noopener noreferrer" className="social-link">
                            {url.length > 50 ? url.substring(0, 50) + '...' : url}
                          </a>
                        </div>
                      )
                    ))}
                  </div>
                </div>
              )}

              {/* Prioritized Websites */}
              {prioritizedWebsites.length > 0 && (
                <div className="spy-data-section prioritized-websites">
                  <div className="section-header">
                    <h3>🎯 High-Priority Websites for Contact Data</h3>
                  </div>
                  <div className="prioritized-websites-list">
                    {prioritizedWebsites.map((website, index) => (
                      <div key={index} className={`prioritized-website-item priority-${website.priority}`}>
                        <div className="website-header">
                          <div className="priority-badge">#{website.priority}</div>
                          <a href={website.url} target="_blank" rel="noopener noreferrer" className="website-url">
                            {website.url}
                          </a>
                        </div>
                        <div className="website-reasoning">{website.reasoning}</div>
                        {website.expectedData && website.expectedData.length > 0 && (
                          <div className="expected-data">
                            <strong>Expected Data:</strong>
                            <div className="data-tags">
                              {website.expectedData.map((dataType, idx) => (
                                <span key={idx} className="data-tag">{dataType}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Scraped Website Data */}
          {scrapingResults && (
            <div className="spy-data-section scraped-data">
              <div className="section-header">
                <h3>🌐 Website Scraping Results</h3>
                <div className="scraping-status">
                  {scrapingResults.success ? (
                    <span className="status-success">✅ Successfully scraped</span>
                  ) : (
                    <span className="status-error">❌ Scraping failed</span>
                  )}
                  <span className="scraped-url">{scrapingResults.url}</span>
                </div>
              </div>
              
              {scrapingResults.success && scrapedData && (
                <div className="scraped-content">
                  {/* Contact Information Found */}
                  {(scrapedData.emails?.length > 0 || scrapedData.phones?.length > 0) && (
                    <div className="scraped-section contact-data">
                      <h4>📞 Contact Information</h4>
                      <div className="contact-items">
                        {scrapedData.emails?.length > 0 && (
                          <div className="contact-item">
                            <strong>📧 Emails ({scrapedData.emails.length}):</strong>
                            <div className="email-list">
                              {scrapedData.emails.map((email, index) => (
                                <EmailValue key={index} email={email} blockedSet={blockedSet} className="email-tag" />
                              ))}
                            </div>
                          </div>
                        )}
                        {scrapedData.phones?.length > 0 && (
                          <div className="contact-item">
                            <strong>📱 Phones ({scrapedData.phones.length}):</strong>
                            <div className="phone-list">
                              {scrapedData.phones.map((phone, index) => (
                                <span key={index} className="phone-tag">{phone}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Social Media Found on Website */}
                  {Object.values(scrapedData.socialLinks || {}).some(Boolean) && (
                    <div className="scraped-section social-scraped">
                      <h4>📱 Social Media Found on Website</h4>
                      <div className="scraped-social-list">
                        {Object.entries(scrapedData.socialLinks).map(([platform, url]) => (
                          url && (
                            <div key={platform} className="scraped-social-item">
                              <span className="platform-name">{platform.charAt(0).toUpperCase() + platform.slice(1)}:</span>
                              <a href={url} target="_blank" rel="noopener noreferrer" className="social-link">
                                {url.length > 60 ? url.substring(0, 60) + '...' : url}
                              </a>
                            </div>
                          )
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Website Metadata */}
                  <div className="scraped-section website-info">
                    <h4>ℹ️ Website Information</h4>
                    <div className="website-metadata">
                      <div className="metadata-item">
                        <strong>Title:</strong> {scrapedData.title || 'No title found'}
                      </div>
                      {scrapedData.description && (
                        <div className="metadata-item">
                          <strong>Description:</strong> {scrapedData.description}
                        </div>
                      )}
                      <div className="metadata-item">
                        <strong>Page Type:</strong> {scrapedData.metadata?.pageType || 'unknown'}
                      </div>
                      <div className="metadata-item">
                        <strong>Has Contact Form:</strong> {scrapedData.metadata?.hasContactForm ? 'Yes' : 'No'}
                      </div>
                      {scrapedData.contactInfo?.addresses?.length > 0 && (
                        <div className="metadata-item">
                          <strong>Addresses Found:</strong> {scrapedData.contactInfo.addresses.join(', ')}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Key Content Preview - Headings */}
                  {scrapedData.content?.headings?.length > 0 && (
                    <div className="scraped-section content-preview">
                      <h4>📝 Content Preview - Headings</h4>
                      <div className="headings-preview">
                        <strong>Main Headings:</strong>
                        <ul>
                          {scrapedData.content.headings.slice(0, 8).map((heading, index) => (
                            <li key={index} className={`heading-${heading.level}`}>
                              <span className="heading-level">{heading.level}:</span> {heading.text}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* Raw Content Preview - Complete Page Content */}
                  {scrapedData.content?.rawContent?.length > 0 && (
                    <div className="scraped-section raw-content-preview">
                      <h4>📄 Complete Page Content ({scrapedData.content.rawContent.length} chunks from {scrapedData.metadata?.scrapedPages?.length || 1} pages)</h4>
                      <div className="raw-content-container">
                        {scrapedData.content.rawContent.slice(0, 20).map((chunk, index) => (
                          <div key={index} className="content-chunk">
                            <div className="chunk-header">
                              <span className={`page-type-badge ${chunk.pageType}`}>{chunk.pageType}</span>
                              <span className="chunk-info">Chunk {chunk.chunkIndex + 1}</span>
                              <span className="content-length">{chunk.text.length} chars</span>
                            </div>
                            <div className="chunk-text">
                              {chunk.text.length > 500 ? (
                                <>
                                  {chunk.text.substring(0, 500)}
                                  <span className="text-more">... ({chunk.text.length - 500} more characters)</span>
                                </>
                              ) : (
                                chunk.text
                              )}
                            </div>
                          </div>
                        ))}
                        {scrapedData.content.rawContent.length > 20 && (
                          <div className="content-chunk more-content">
                            <div className="chunk-text">
                              <em>+ {scrapedData.content.rawContent.length - 20} more content chunks available for AI analysis...</em>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Legacy Paragraphs Preview (if no rawContent available) */}
                  {(!scrapedData.content?.rawContent || scrapedData.content.rawContent.length === 0) && 
                   scrapedData.content?.paragraphs?.length > 0 && (
                    <div className="scraped-section content-preview">
                      <h4>📝 Text Content</h4>
                      <div className="paragraphs-preview">
                        {scrapedData.content.paragraphs.slice(0, 10).map((paragraph, index) => (
                          <div key={index} className="paragraph-item">
                            <strong>Paragraph {index + 1}:</strong>
                            <p>{paragraph.length > 300 ? paragraph.substring(0, 300) + '...' : paragraph}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {!scrapingResults.success && (
                <div className="scraping-error">
                  <p><strong>Error:</strong> {scrapingResults.error}</p>
                  <p>The website could not be scraped at this time.</p>
                </div>
              )}
            </div>
          )}

          {/* Email Search Results - New */}
          {intelligenceData.emailSearchResults && (
            <div className="spy-data-section email-search-results">
              <div className="section-header">
                <h3>📧 Email-Focused Search Results ({intelligenceData.emailSearchResults.organic?.length || 0})</h3>
                {intelligenceData.emailsFoundInSearch?.length > 0 && (
                  <div className="emails-found-badge">
                    🎯 {intelligenceData.emailsFoundInSearch.length} Email{intelligenceData.emailsFoundInSearch.length !== 1 ? 's' : ''} Found!
                  </div>
                )}
              </div>
              
              {/* Show found emails prominently */}
              {intelligenceData.emailsFoundInSearch?.length > 0 && (
                <div className="found-emails-section">
                  <h4>📬 Emails Found in Search Results:</h4>
                  <div className="found-emails-list">
                    {intelligenceData.emailsFoundInSearch.map((email, index) => {
                      const isBlocked = typeof email === 'string' && blockedSet.has(email.trim().toLowerCase());
                      return (
                        <div key={index} className={`found-email-item${isBlocked ? ' is-blacklisted' : ''}`}>
                          <span className="email-icon">{isBlocked ? '🚫' : '📧'}</span>
                          <EmailValue email={email} blockedSet={blockedSet} className="email-address" />
                          <span className="email-source">{isBlocked ? 'Will be skipped' : 'From Search'}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              <div className="search-results-list">
                {(intelligenceData.emailSearchResults.organic || []).map((result, index) => (
                  <div key={index} className="search-result-item email-search">
                    <div className="result-header">
                      <a href={result.link} target="_blank" rel="noopener noreferrer" className="result-title">
                        {result.title}
                      </a>
                      <div className="result-url">{result.displayLink || result.link}</div>
                    </div>
                    <div className="result-snippet">{result.snippet}</div>
                    {result.sitelinks && result.sitelinks.length > 0 && (
                      <div className="result-sitelinks">
                        <strong>Sitelinks:</strong>
                        <ul>
                          {result.sitelinks.map((sitelink, slIndex) => (
                            <li key={slIndex}>
                              <a href={sitelink.link} target="_blank" rel="noopener noreferrer">
                                {sitelink.title}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Standard Search Results */}
          <div className="spy-data-section search-results">
            <div className="section-header">
              <h3>🔍 Standard Search Results ({results.length})</h3>
            </div>
            <div className="search-results-list">
              {results.map((result, index) => (
                <div key={index} className="search-result-item">
                  <div className="result-header">
                    <a href={result.link} target="_blank" rel="noopener noreferrer" className="result-title">
                      {result.title}
                    </a>
                    <div className="result-url">{result.displayLink || result.link}</div>
                  </div>
                  <div className="result-snippet">{result.snippet}</div>
                  {result.sitelinks && result.sitelinks.length > 0 && (
                    <div className="result-sitelinks">
                      <strong>Sitelinks:</strong>
                      <ul>
                        {result.sitelinks.map((sitelink, slIndex) => (
                          <li key={slIndex}>
                            <a href={sitelink.link} target="_blank" rel="noopener noreferrer">
                              {sitelink.title}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Raw Data Preview (for debugging) */}
          <div className="spy-data-section raw-data">
            <div className="section-header">
              <h3>🔧 Raw API Response (Debug)</h3>
            </div>
            <div className="raw-data-content">
              <pre>{JSON.stringify(rawSearchResults || searchResults, null, 2)}</pre>
            </div>
          </div>
        </div>

        <div className="spy-preview-actions">
          <button className="spy-action-button secondary" onClick={onClose}>
            Close
          </button>
          <button className="spy-action-button primary" onClick={() => onApply(intelligenceData)}>
            📊 Apply All Data to Contact
          </button>
        </div>
      </div>
    </div>
  );
};

export default SpyDataPreview; 